<!-- AddPlotModal.vue -->
<template>
  <div class="modal fade add-plot-modal" tabindex="-1" role="dialog" aria-labelledby="addPlotModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header text-white">
          <h5 class="modal-title text-uppercase fw-bold" id="addPlotModalLabel">
            <i class="mdi mdi-home-plus-outline me-2"></i>{{ title || 'Add New Property' }}
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body p-0">
          <!-- Progress Indicator -->
          <div class="bg-light border-bottom p-3">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <div class="step-indicator me-3" :class="{ 'active': currentStep >= 1, 'completed': currentStep > 1 }">
                  <i class="mdi mdi-map-marker"></i>
                </div>
                <div class="step-indicator me-3" :class="{ 'active': currentStep >= 2, 'completed': currentStep > 2 }">
                  <i class="mdi mdi-account"></i>
                </div>
                <div class="step-indicator me-3" :class="{ 'active': currentStep >= 3, 'completed': currentStep > 3 }">
                  <i class="mdi mdi-account-multiple"></i>
                </div>
                <div class="step-indicator" :class="{ 'active': currentStep >= 4, 'completed': currentStep > 4 }">
                  <i class="mdi mdi-currency-usd"></i>
                </div>
              </div>
              <small class="text-muted">Step {{ currentStep }} of 4</small>
            </div>
          </div>

          <!-- Tabbed Content -->
          <ul class="nav nav-tabs nav-justified" id="plotTabs" role="tablist">
            <li class="nav-item" role="presentation">
              <button
                  class="nav-link fw-semibold"
                  :class="{ 'active': currentStep === 1 }"
                  @click="currentStep = 1"
                  type="button">
                <i class="mdi mdi-map-marker-outline me-1"></i>Property Details
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                  class="nav-link fw-semibold"
                  :class="{ 'active': currentStep === 2 }"
                  @click="currentStep = 2"
                  type="button">
                <i class="mdi mdi-account-outline me-1"></i>Owner Details
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                  class="nav-link fw-semibold"
                  :class="{ 'active': currentStep === 3 }"
                  @click="currentStep = 3"
                  type="button">
                <i class="mdi mdi-account-multiple-outline me-1"></i>Co-Owner
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                  class="nav-link fw-semibold"
                  :class="{ 'active': currentStep === 4 }"
                  @click="currentStep = 4"
                  type="button">
                <i class="mdi mdi-currency-usd me-1"></i>Financial Details
              </button>
            </li>
          </ul>

          <div class="tab-content p-4">
            <!-- Step 1: Property Details -->
            <div v-show="currentStep === 1" class="step-content">
              <div class="row">
                <div class="col-12 mb-4">
                  <h6 class="fw-bold text-primary mb-3">
                    <i class="mdi mdi-information-outline me-2"></i>Basic Property Information
                  </h6>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      LR Number <span class="text-danger">*</span>
                    </label>
                    <input
                        v-model="localProperty.LRNumber"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors.LRNumber }"
                        placeholder="Enter LR Number"
                        :readonly="isEditing">
                    <div v-if="errors.LRNumber" class="invalid-feedback">{{ errors.LRNumber }}</div>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">UPN Number</label>
                    <input
                        v-model="localProperty.UPN"
                        type="text"
                        class="form-control"
                        placeholder="Enter UPN Number">
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Plot Size (Ha)</label>
                    <input
                        v-model="localProperty.percelSize"
                        type="number"
                        step="0.01"
                        class="form-control"
                        placeholder="Enter size in hectares">
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Sub County <span class="text-danger">*</span>
                    </label>
                    <select
                        class="form-select"
                        :class="{ 'is-invalid': errors.subCounty }"
                        @change="handleSubCountyChange"
                        v-model="selectedSubCounty">
                      <option value="">Select Sub County</option>
                      <option v-for="(item, index) in subCounties" :key="index" :value="item">
                        {{ item.subCountyName }}
                      </option>
                    </select>
                    <div v-if="errors.subCounty" class="invalid-feedback">{{ errors.subCounty }}</div>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Ward</label>
                    <select class="form-select" v-model="selectedWard">
                      <option value="">Select Ward</option>
                      <option v-for="(item, index) in wards" :key="index" :value="item">
                        {{ item.wardName }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Zone</label>
                    <input
                        v-model="localProperty.zone"
                        type="text"
                        class="form-control"
                        placeholder="Enter zone">
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Block</label>
                    <select class="form-select" v-model="selectedBlock">
                      <option value="">Select Block</option>
                      <option v-for="(item, index) in blocks" :key="index" :value="item.block">
                        {{ item.block }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Land Use</label>
                    <select
                        class="form-select"
                        v-model="selectedUseName"
                        :disabled="isEditing"
                        :class="{ 'bg-light text-muted': isEditing }">
                      <option value="">Select Land Use</option>
                      <option v-for="(item, index) in uses" :key="index" :value="item.name">
                        {{ item.name }}
                      </option>
                    </select>
                    <small v-if="isEditing" class="text-muted">Land Use cannot be changed when editing</small>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Status</label>
                    <select
                        class="form-select bg-light text-muted"
                        v-model="localProperty.status"
                        disabled>
                      <option value="Active">Active</option>
                      <option value="Suspended">Suspended</option>
                      <option value="Rate Struck">Rate Struck</option>
                      <option value="Subdivided">Subdivided</option>
                    </select>
                    <small class="text-muted">Status is automatically set to Active for new properties</small>
                  </div>
                </div>

                <div class="col-12">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Physical Address</label>
                    <textarea
                        v-model="localProperty.percelAddress"
                        class="form-control"
                        rows="2"
                        placeholder="Enter complete physical address"></textarea>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Longitude</label>
                    <input
                        v-model="localProperty.lng"
                        type="text"
                        class="form-control"
                        placeholder="Longitude coordinate">
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Latitude</label>
                    <input
                        v-model="localProperty.lat"
                        type="text"
                        class="form-control"
                        placeholder="Latitude coordinate">
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 2: Owner Details -->
            <div v-show="currentStep === 2" class="step-content">
              <div class="row">
                <div class="col-12 mb-4">
                  <h6 class="fw-bold text-primary mb-3">
                    <i class="mdi mdi-account-outline me-2"></i>Primary Owner Information
                  </h6>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Owner Name <span class="text-danger">*</span>
                    </label>
                    <input
                        v-model="localProperty.ownerNames"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors.ownerNames }"
                        placeholder="Enter full name">
                    <div v-if="errors.ownerNames" class="invalid-feedback">{{ errors.ownerNames }}</div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">National ID Number</label>
                    <input
                        v-model="localProperty.ownerID"
                        type="text"
                        class="form-control"
                        placeholder="Enter ID number">
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Mobile Number</label>
                    <input
                        v-model="localProperty.ownerPhoneNo"
                        type="tel"
                        class="form-control"
                        placeholder="e.g., 0712345678">
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Email Address</label>
                    <input
                        v-model="localProperty.ownerEmail"
                        type="email"
                        class="form-control"
                        placeholder="Enter email address">
                  </div>
                </div>

                <div class="col-12 mb-3">
                  <h6 class="fw-bold text-secondary mb-3">Alternative Identification</h6>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Kenyan Passport</label>
                    <input
                        v-model="localProperty.ownerPassport"
                        type="text"
                        class="form-control"
                        placeholder="Passport number">
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Driving License</label>
                    <input
                        v-model="localProperty.ownerDrivingLicence"
                        type="text"
                        class="form-control"
                        placeholder="License number">
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Foreign Passport</label>
                    <input
                        v-model="localProperty.ownerPassportNonKenyan"
                        type="text"
                        class="form-control"
                        placeholder="Foreign passport">
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Registration Certificate</label>
                    <input
                        v-model="localProperty.registrationCertificate"
                        type="text"
                        class="form-control"
                        placeholder="Certificate number">
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">PIN Number</label>
                    <input
                        v-model="localProperty.pinNumber"
                        type="text"
                        class="form-control"
                        placeholder="KRA PIN number">
                  </div>
                </div>

                <div class="col-12 mb-3">
                  <h6 class="fw-bold text-secondary mb-3">Postal Address</h6>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">P.O. Box</label>
                    <input
                        v-model="localProperty.boxNo"
                        type="text"
                        class="form-control"
                        placeholder="Box number">
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Postal Code</label>
                    <input
                        v-model="localProperty.postalCode"
                        type="text"
                        class="form-control"
                        placeholder="Postal code">
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Town</label>
                    <input
                        v-model="localProperty.town"
                        type="text"
                        class="form-control"
                        placeholder="Town/City">
                  </div>
                </div>

                <div class="col-12">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Physical Address</label>
                    <textarea
                        v-model="localProperty.physicalAddress"
                        class="form-control"
                        rows="2"
                        placeholder="Complete physical address"></textarea>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: Co-Owner Details -->
            <div v-show="currentStep === 3" class="step-content">
              <div class="row">
                <div class="col-12 mb-4">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="fw-bold text-primary mb-0">
                      <i class="mdi mdi-account-multiple-outline me-2"></i>Co-Owner Information
                    </h6>
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" v-model="hasCoOwner" id="hasCoOwnerSwitch">
                      <label class="form-check-label fw-semibold" for="hasCoOwnerSwitch">
                        Add Co-Owner
                      </label>
                    </div>
                  </div>
                </div>

                <template v-if="hasCoOwner">
                  <div class="col-md-6">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">Co-Owner Name</label>
                      <input
                          v-model="localProperty.coOwnerNames"
                          type="text"
                          class="form-control"
                          placeholder="Enter full name">
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">National ID Number</label>
                      <input
                          v-model="localProperty.coOwnerID"
                          type="text"
                          class="form-control"
                          placeholder="Enter ID number">
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">Mobile Number</label>
                      <input
                          v-model="localProperty.coOwnerPhoneNo"
                          type="tel"
                          class="form-control"
                          placeholder="e.g., 0712345678">
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">Email Address</label>
                      <input
                          v-model="localProperty.coOwnerEmail"
                          type="email"
                          class="form-control"
                          placeholder="Enter email address">
                    </div>
                  </div>

                  <div class="col-12 mb-3">
                    <h6 class="fw-bold text-secondary mb-3">Alternative Identification</h6>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">Kenyan Passport</label>
                      <input
                          v-model="localProperty.coOwnerPassport"
                          type="text"
                          class="form-control"
                          placeholder="Passport number">
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">Driving License</label>
                      <input
                          v-model="localProperty.coOwnerDrivingLicence"
                          type="text"
                          class="form-control"
                          placeholder="License number">
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">Foreign Passport</label>
                      <input
                          v-model="localProperty.coOwnerPassportNonKenyan"
                          type="text"
                          class="form-control"
                          placeholder="Foreign passport">
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">Registration Certificate</label>
                      <input
                          v-model="localProperty.coOwnerRegistrationCertificate"
                          type="text"
                          class="form-control"
                          placeholder="Certificate number">
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">PIN Number</label>
                      <input
                          v-model="localProperty.coOwnerPinNumber"
                          type="text"
                          class="form-control"
                          placeholder="KRA PIN number">
                    </div>
                  </div>

                  <div class="col-12 mb-3">
                    <h6 class="fw-bold text-secondary mb-3">Postal Address</h6>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">P.O. Box</label>
                      <input
                          v-model="localProperty.coOwnerBoxNo"
                          type="text"
                          class="form-control"
                          placeholder="Box number">
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">Postal Code</label>
                      <input
                          v-model="localProperty.coOwnerPostalCode"
                          type="text"
                          class="form-control"
                          placeholder="Postal code">
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">Town</label>
                      <input
                          v-model="localProperty.coOwnerTown"
                          type="text"
                          class="form-control"
                          placeholder="Town/City">
                    </div>
                  </div>

                  <div class="col-12">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">Physical Address</label>
                      <textarea
                          v-model="localProperty.coOwnerPhysicalAddress"
                          class="form-control"
                          rows="2"
                          placeholder="Complete physical address"></textarea>
                    </div>
                  </div>
                </template>

                <div v-else class="col-12">
                  <div class="text-center text-muted py-5">
                    <i class="mdi mdi-account-off display-1 opacity-25"></i>
                    <p class="mt-3 mb-0">No co-owner information will be added</p>
                    <small>Toggle the switch above to add co-owner details</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 4: Financial Details -->
            <div v-show="currentStep === 4" class="step-content">
              <div class="row">
                <div class="col-12 mb-4">
                  <h6 class="fw-bold text-primary mb-3">
                    <i class="mdi mdi-currency-usd me-2"></i>Financial Information
                  </h6>
                </div>

                <!-- Annual Charges Section -->
                <div class="col-12 mb-4">
                  <div class="card border-0 shadow-sm">
                    <div class="card-header bg-success text-white">
                      <h6 class="mb-0 fw-bold" style="color: white">
                        <i class="mdi mdi-calendar-clock me-2"></i>Annual Charges
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-4">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Annual Land Rates (KES)</label>
                            <input
                                v-model.number="localProperty.annualLandRates"
                                type="number"
                                step="0.01"
                                class="form-control"
                                placeholder="0.00"
                                @input="calculateTotals">
                          </div>
                        </div>

                        <div class="col-md-4">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Annual Ground Rent (KES)</label>
                            <input
                                v-model.number="localProperty.annualGroundRent"
                                type="number"
                                step="0.01"
                                class="form-control"
                                placeholder="0.00"
                                @input="calculateTotals">
                          </div>
                        </div>

                        <div class="col-md-4">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Total Annual Charges (KES)</label>
                            <input
                                v-model.number="localProperty.annualCharges"
                                type="number"
                                step="0.01"
                                class="form-control bg-light"
                                placeholder="0.00"
                                readonly>
                            <small class="text-muted">Auto-calculated</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ============================================== -->
                <!-- CURRENT YEAR PAYMENT STATUS CONFIRMATION -->
                <!-- ============================================== -->
                <div class="col-12 mb-4">
                  <div class="card border-2" :class="currentYearPaymentStatusClass">
                    <div class="card-header bg-dark text-white">
                      <h6 class="mb-0 fw-bold" style="color: white">
                        <i class="mdi mdi-alert-circle me-2"></i>
                        Current Year ({{ currentYear }}) Payment Status Confirmation
                        <span class="text-danger">*</span>
                      </h6>
                    </div>
                    <div class="card-body">
                      <!-- Important Notice -->
                      <div class="alert alert-warning border-warning mb-4">
                        <div class="d-flex align-items-start">
                          <i class="mdi mdi-alert-outline me-3" style="font-size: 24px;"></i>
                          <div>
                            <h6 class="fw-bold mb-2">IMPORTANT: Please Confirm Payment Status</h6>
                            <p class="mb-0">
                              Since this property data is being imported from another system, you must confirm
                              whether the property owner has <strong>PAID</strong> or <strong>NOT PAID</strong>
                              their Land Rates and Ground Rent for the current year ({{ currentYear }}).
                            </p>
                            <p class="mb-0 mt-2">
                              <strong>This selection affects the total arrears calculation:</strong>
                            </p>
                            <ul class="mb-0 mt-1">
                              <li>
                                <strong>If NOT PAID:</strong> The following will be added to arrears:
                                <ul class="mb-0">
                                  <li>Annual Land Rates: <span class="text-danger fw-bold">KES {{ numberFormat(localProperty.annualLandRates || 0) }}</span></li>
                                  <li>Annual Ground Rent: <span class="text-danger fw-bold">KES {{ numberFormat(localProperty.annualGroundRent || 0) }}</span></li>
                                  <li>Total: <span class="text-danger fw-bold">KES {{ numberFormat((localProperty.annualLandRates || 0) + (localProperty.annualGroundRent || 0)) }}</span></li>
                                </ul>
                              </li>
                              <li><strong>If PAID:</strong> Only the arrears amounts you enter below will be used</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <!-- Question -->
                      <div class="text-center mb-4">
                        <h5 class="fw-bold text-dark mb-3">
                          <i class="mdi mdi-help-circle-outline me-2"></i>
                          Has this property owner paid their Land Rates & Ground Rent for {{ currentYear }}?
                        </h5>
                      </div>

                      <!-- Radio Button Options -->
                      <div class="row justify-content-center">
                        <div class="col-md-5">
                          <div
                              class="card h-100 cursor-pointer payment-option-card"
                              :class="{ 'border-success bg-success-light selected': currentYearPaymentStatus === 'PAID' }"
                              @click="setCurrentYearPaymentStatus('PAID')">
                            <div class="card-body text-center p-4">
                              <div class="form-check d-flex justify-content-center align-items-center">
                                <input
                                    class="form-check-input me-3"
                                    type="radio"
                                    name="currentYearPayment"
                                    id="paymentStatusPaid"
                                    value="PAID"
                                    v-model="currentYearPaymentStatus"
                                    style="transform: scale(1.5);">
                                <label class="form-check-label" for="paymentStatusPaid">
                                  <i class="mdi mdi-check-circle text-success" style="font-size: 48px;"></i>
                                  <h5 class="fw-bold text-success mt-2 mb-1">YES, PAID</h5>
                                  <p class="text-muted mb-0 small">
                                    The property owner has already paid for {{ currentYear }}
                                  </p>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="col-md-5">
                          <div
                              class="card h-100 cursor-pointer payment-option-card"
                              :class="{ 'border-danger bg-danger-light selected': currentYearPaymentStatus === 'NOT_PAID' }"
                              @click="setCurrentYearPaymentStatus('NOT_PAID')">
                            <div class="card-body text-center p-4">
                              <div class="form-check d-flex justify-content-center align-items-center">
                                <input
                                    class="form-check-input me-3"
                                    type="radio"
                                    name="currentYearPayment"
                                    id="paymentStatusNotPaid"
                                    value="NOT_PAID"
                                    v-model="currentYearPaymentStatus"
                                    style="transform: scale(1.5);">
                                <label class="form-check-label" for="paymentStatusNotPaid">
                                  <i class="mdi mdi-close-circle text-danger" style="font-size: 48px;"></i>
                                  <h5 class="fw-bold text-danger mt-2 mb-1">NO, NOT PAID</h5>
                                  <p class="text-muted mb-0 small">
                                    The property owner has NOT paid for {{ currentYear }}
                                  </p>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Validation Error -->
                      <div v-if="errors.currentYearPaymentStatus" class="text-center mt-3">
                        <span class="text-danger fw-bold">
                          <i class="mdi mdi-alert me-1"></i>
                          {{ errors.currentYearPaymentStatus }}
                        </span>
                      </div>

                      <!-- Selection Result Display -->
                      <div v-if="currentYearPaymentStatus" class="mt-4">
                        <div
                            class="alert"
                            :class="currentYearPaymentStatus === 'PAID' ? 'alert-success' : 'alert-danger'">
                          <div class="d-flex align-items-center">
                            <i
                                class="mdi me-3"
                                :class="currentYearPaymentStatus === 'PAID' ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline'"
                                style="font-size: 24px;"></i>
                            <div>
                              <strong v-if="currentYearPaymentStatus === 'PAID'">
                                ✓ Payment Confirmed: Property owner HAS PAID for {{ currentYear }}
                              </strong>
                              <div v-else>
                                <strong>✗ Payment NOT Received for {{ currentYear }}:</strong>
                                <ul class="mb-0 mt-1">
                                  <li>Annual Land Rates: KES {{ numberFormat(localProperty.annualLandRates || 0) }} → Added to Land Rates Arrears</li>
                                  <li>Annual Ground Rent: KES {{ numberFormat(localProperty.annualGroundRent || 0) }} → Added to Ground Rent Arrears</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Arrears Section -->
                <div class="col-12 mb-4">
                  <div class="card border-0 shadow-sm">
                    <div class="card-header bg-danger text-white">
                      <h6 class="mb-0 fw-bold" style="color: white">
                        <i class="mdi mdi-alert-circle me-2"></i>Outstanding Arrears (Previous Years)
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="alert alert-info mb-3">
                        <i class="mdi mdi-information me-2"></i>
                        Enter the arrears carried forward from <strong>previous years</strong> (before {{ currentYear }}).
                        <span v-if="currentYearPaymentStatus === 'NOT_PAID'" class="text-danger fw-bold">
                          The current year's annual charges will be automatically added to the totals.
                        </span>
                      </div>
                      <div class="row">
                        <!-- Land Rates Arrears Input -->
                        <div class="col-md-6">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">
                              Land Rates Arrears - Previous Years (KES)
                            </label>
                            <input
                                v-model.number="enteredLandRatesArrears"
                                type="number"
                                step="0.01"
                                class="form-control"
                                placeholder="0.00"
                                @input="calculateTotals">
                            <small class="text-muted">Arrears from years before {{ currentYear }}</small>
                          </div>
                        </div>

                        <!-- Ground Rent Arrears Input -->
                        <div class="col-md-6">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">
                              Ground Rent Arrears - Previous Years (KES)
                            </label>
                            <input
                                v-model.number="enteredGroundRentArrears"
                                type="number"
                                step="0.01"
                                class="form-control"
                                placeholder="0.00"
                                @input="calculateTotals">
                            <small class="text-muted">Arrears from years before {{ currentYear }}</small>
                          </div>
                        </div>

                        <!-- Show calculated totals if current year not paid -->
                        <div v-if="currentYearPaymentStatus === 'NOT_PAID'" class="col-12">
                          <div class="row">
                            <!-- Land Rates Calculation -->
                            <div class="col-md-6">
                              <div class="alert alert-warning mb-3">
                                <h6 class="fw-bold mb-2">
                                  <i class="mdi mdi-calculator me-1"></i> Land Rates Arrears Calculation
                                </h6>
                                <div class="row align-items-center">
                                  <div class="col-4 text-center">
                                    <small class="text-muted d-block">Previous Arrears</small>
                                    <strong>KES {{ numberFormat(enteredLandRatesArrears || 0) }}</strong>
                                  </div>
                                  <div class="col-1 text-center">
                                    <i class="mdi mdi-plus-circle text-warning" style="font-size: 20px;"></i>
                                  </div>
                                  <div class="col-4 text-center">
                                    <small class="text-muted d-block">{{ currentYear }} Annual</small>
                                    <strong>KES {{ numberFormat(localProperty.annualLandRates || 0) }}</strong>
                                  </div>
                                  <div class="col-1 text-center">
                                    <i class="mdi mdi-equal text-warning" style="font-size: 20px;"></i>
                                  </div>
                                  <div class="col-2 text-center">
                                    <small class="text-muted d-block">Total</small>
                                    <strong class="text-danger">KES {{ numberFormat(localProperty.landRatesArrears) }}</strong>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Ground Rent Calculation -->
                            <div class="col-md-6">
                              <div class="alert alert-warning mb-3">
                                <h6 class="fw-bold mb-2">
                                  <i class="mdi mdi-calculator me-1"></i> Ground Rent Arrears Calculation
                                </h6>
                                <div class="row align-items-center">
                                  <div class="col-4 text-center">
                                    <small class="text-muted d-block">Previous Arrears</small>
                                    <strong>KES {{ numberFormat(enteredGroundRentArrears || 0) }}</strong>
                                  </div>
                                  <div class="col-1 text-center">
                                    <i class="mdi mdi-plus-circle text-warning" style="font-size: 20px;"></i>
                                  </div>
                                  <div class="col-4 text-center">
                                    <small class="text-muted d-block">{{ currentYear }} Annual</small>
                                    <strong>KES {{ numberFormat(localProperty.annualGroundRent || 0) }}</strong>
                                  </div>
                                  <div class="col-1 text-center">
                                    <i class="mdi mdi-equal text-warning" style="font-size: 20px;"></i>
                                  </div>
                                  <div class="col-2 text-center">
                                    <small class="text-muted d-block">Total</small>
                                    <strong class="text-danger">KES {{ numberFormat(localProperty.groundRentArrears) }}</strong>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Show final arrears values (read-only) -->
                        <div class="col-md-6">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">
                              <i class="mdi mdi-arrow-right-bold text-success me-1"></i>
                              Final Land Rates Arrears (KES)
                            </label>
                            <input
                                v-model.number="localProperty.landRatesArrears"
                                type="number"
                                step="0.01"
                                class="form-control bg-light fw-bold"
                                :class="localProperty.landRatesArrears > 0 ? 'text-danger border-danger' : ''"
                                placeholder="0.00"
                                readonly>
                            <small class="text-muted">Auto-calculated based on payment status</small>
                          </div>
                        </div>

                        <div class="col-md-6">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">
                              <i class="mdi mdi-arrow-right-bold text-success me-1"></i>
                              Final Ground Rent Arrears (KES)
                            </label>
                            <input
                                v-model.number="localProperty.groundRentArrears"
                                type="number"
                                step="0.01"
                                class="form-control bg-light fw-bold"
                                :class="localProperty.groundRentArrears > 0 ? 'text-danger border-danger' : ''"
                                placeholder="0.00"
                                readonly>
                            <small class="text-muted">Auto-calculated based on payment status</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Penalties & Interest Section -->
                <div class="col-12 mb-4">
                  <div class="card border-0 shadow-sm">
                    <div class="card-header bg-warning text-dark">
                      <h6 class="mb-0 fw-bold" style="color: white">
                        <i class="mdi mdi-percent me-2"></i>Penalties & Interest
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-6">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Accumulated Penalty (KES)</label>
                            <input
                                v-model.number="localProperty.accumPenalty"
                                type="number"
                                step="0.01"
                                class="form-control"
                                placeholder="0.00"
                                @input="calculateTotals">
                          </div>
                        </div>

                        <div class="col-md-6">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Accumulated Interest (KES)</label>
                            <input
                                v-model.number="localProperty.accumInterest"
                                type="number"
                                step="0.01"
                                class="form-control"
                                placeholder="0.00"
                                @input="calculateTotals">
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Payments & Balance Section -->
                <div class="col-12 mb-4">
                  <div class="card border-0 shadow-sm">
                    <div class="card-header bg-primary text-white">
                      <h6 class="mb-0 fw-bold" style="color: white">
                        <i class="mdi mdi-calculator me-2"></i>Payments & Balance
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-4">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Total Payments Made (KES)</label>
                            <input
                                v-model.number="localProperty.payments"
                                type="number"
                                step="0.01"
                                class="form-control"
                                placeholder="0.00"
                                @input="calculateTotals">
                          </div>
                        </div>

                        <div class="col-md-4">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Outstanding Arrears (KES)</label>
                            <input
                                v-model.number="localProperty.outstandingArrears"
                                type="number"
                                step="0.01"
                                class="form-control bg-light"
                                placeholder="0.00"
                                readonly>
                            <small class="text-muted">Auto-calculated (synced with Current Balance)</small>
                          </div>
                        </div>

                        <div class="col-md-4">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Current Balance (KES)</label>
                            <input
                                v-model.number="localProperty.currentBalance"
                                type="number"
                                step="0.01"
                                class="form-control bg-light"
                                :class="currentBalanceClass"
                                placeholder="0.00"
                                readonly>
                            <small class="text-muted">Auto-calculated</small>
                          </div>
                        </div>
                      </div>

                      <!-- Financial Summary -->
                      <div class="row mt-3">
                        <div class="col-12">
                          <div class="alert alert-info">
                            <h6 class="fw-bold mb-2">
                              <i class="mdi mdi-information-outline me-2"></i>Financial Summary
                            </h6>
                            <div class="row text-center">
                              <div class="col-md-3">
                                <small class="text-muted d-block">Total Annual</small>
                                <strong class="text-success">KES {{ numberFormat(localProperty.annualCharges || 0) }}</strong>
                              </div>
                              <div class="col-md-3">
                                <small class="text-muted d-block">Total Arrears</small>
                                <strong class="text-danger">KES {{ numberFormat(totalArrears) }}</strong>
                              </div>
                              <div class="col-md-3">
                                <small class="text-muted d-block">Total Payments</small>
                                <strong class="text-primary">KES {{ numberFormat(localProperty.payments || 0) }}</strong>
                              </div>
                              <div class="col-md-3">
                                <small class="text-muted d-block">Balance Status</small>
                                <span :class="balanceStatusClass">
                                  {{ balanceStatus }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer bg-light">
          <div class="d-flex justify-content-between w-100">
            <div>
              <button
                  v-if="currentStep > 1"
                  type="button"
                  class="btn btn-secondary"
                  @click="currentStep--">
                <i class="mdi mdi-arrow-left me-1"></i>Previous
              </button>
            </div>

            <div>
              <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">
                <i class="mdi mdi-close me-1"></i>Cancel
              </button>

              <button
                  v-if="currentStep < 4"
                  type="button"
                  class="btn btn-primary"
                  @click="nextStep">
                Next<i class="mdi mdi-arrow-right ms-1"></i>
              </button>

              <button
                  v-if="currentStep === 4"
                  type="button"
                  class="btn btn-success"
                  @click="handleSubmit"
                  :disabled="!isFormValid">
                <i class="mdi mdi-check me-1"></i>Submit Property
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AddPlotModal',
  props: {
    title: {
      type: String,
      default: 'Add New Property'
    },
    property: {
      type: Object,
      default: () => ({})
    },
    blocks: {
      type: Array,
      default: () => []
    },
    uses: {
      type: Array,
      default: () => []
    },
    subCounties: {
      type: Array,
      default: () => []
    },
    wards: {
      type: Array,
      default: () => []
    }
  },
  emits: ['submit', 'get-wards'],
  data() {
    return {
      currentStep: 1,
      hasCoOwner: false,
      errors: {},

      // Current year payment status - null means not selected yet
      currentYearPaymentStatus: null, // 'PAID' or 'NOT_PAID'

      // Separate fields for entered arrears (before adding current year)
      enteredLandRatesArrears: 0,
      enteredGroundRentArrears: 0,

      localProperty: {
        // Initialize all financial properties
        annualLandRates: 0,
        annualGroundRent: 0,
        annualCharges: 0,
        landRatesArrears: 0,
        groundRentArrears: 0,
        accumPenalty: 0,
        accumInterest: 0,
        payments: 0,
        outstandingArrears: 0,
        currentBalance: 0,
        status: 'Active',
        ...this.property
      },
      selectedBlock: '',
      selectedUseName: '',
      selectedSubCounty: null,
      selectedWard: null
    }
  },
  computed: {
    // Get current year
    currentYear() {
      return new Date().getFullYear();
    },

    // Dynamic class for payment status card
    currentYearPaymentStatusClass() {
      if (this.currentYearPaymentStatus === 'PAID') {
        return 'border-success';
      } else if (this.currentYearPaymentStatus === 'NOT_PAID') {
        return 'border-danger';
      }
      return 'border-secondary';
    },

    // Check if we're editing based on title or if property has an ID
    isEditing() {
      return this.title === 'EDIT PLOT' ||
          this.title?.toLowerCase().includes('edit') ||
          (this.property && this.property.id) ||
          (this.property && this.property.LRNumber && Object.keys(this.property).length > 1);
    },

    totalArrears() {
      return (parseFloat(this.localProperty.landRatesArrears) || 0) +
          (parseFloat(this.localProperty.groundRentArrears) || 0) +
          (parseFloat(this.localProperty.accumPenalty) || 0) +
          (parseFloat(this.localProperty.accumInterest) || 0);
    },

    currentBalanceClass() {
      const balance = this.localProperty.currentBalance || 0;
      return balance > 0 ? 'border-danger text-danger' : 'border-success text-success';
    },

    balanceStatus() {
      const balance = this.localProperty.currentBalance || 0;
      return balance > 0 ? 'Outstanding' : 'Paid Up';
    },

    balanceStatusClass() {
      const balance = this.localProperty.currentBalance || 0;
      return balance > 0 ? 'badge bg-danger' : 'badge bg-success';
    },

    isFormValid() {
      return this.localProperty.LRNumber &&
          this.localProperty.ownerNames &&
          this.selectedSubCounty &&
          this.currentYearPaymentStatus !== null; // Must select payment status
    }
  },
  watch: {
    property: {
      handler(newVal) {
        this.localProperty = {
          annualLandRates: 0,
          annualGroundRent: 0,
          annualCharges: 0,
          landRatesArrears: 0,
          groundRentArrears: 0,
          accumPenalty: 0,
          accumInterest: 0,
          payments: 0,
          outstandingArrears: 0,
          currentBalance: 0,
          status: 'Active',
          ...newVal
        };

        // Set selected values if editing
        if (newVal.block) this.selectedBlock = newVal.block;
        if (newVal.useName) this.selectedUseName = newVal.useName;
        if (newVal.coOwnerNames) this.hasCoOwner = true;

        // Set sub county and ward if available
        if (newVal.subCountyID) {
          this.selectedSubCounty = this.subCounties.find(sc => sc.subCountyID === newVal.subCountyID);
        }
        if (newVal.wardID) {
          this.selectedWard = this.wards.find(w => w.wardID === newVal.wardID);
        }

        // If editing, initialize entered arrears from existing data
        if (newVal.landRatesArrears) {
          this.enteredLandRatesArrears = newVal.landRatesArrears;
        }
        if (newVal.groundRentArrears) {
          this.enteredGroundRentArrears = newVal.groundRentArrears;
        }

        this.calculateTotals();
      },
      deep: true,
      immediate: true
    },

    // Watch payment status changes to recalculate
    currentYearPaymentStatus() {
      this.calculateTotals();
    }
  },
  mounted() {
    // Ensure status is set to Active for new properties on component mount
    if (!this.isEditing && !this.localProperty.status) {
      this.localProperty.status = 'Active';
    }
  },
  methods: {
    // Set current year payment status
    setCurrentYearPaymentStatus(status) {
      this.currentYearPaymentStatus = status;
      // Clear error when user makes a selection
      if (this.errors.currentYearPaymentStatus) {
        delete this.errors.currentYearPaymentStatus;
      }
    },

    nextStep() {
      if (this.validateCurrentStep()) {
        this.currentStep++;
      }
    },

    validateCurrentStep() {
      this.errors = {};

      if (this.currentStep === 1) {
        if (!this.localProperty.LRNumber) {
          this.errors.LRNumber = 'LR Number is required';
        }
        if (!this.selectedSubCounty) {
          this.errors.subCounty = 'Sub County is required';
        }
      }

      if (this.currentStep === 2) {
        if (!this.localProperty.ownerNames) {
          this.errors.ownerNames = 'Owner name is required';
        }
      }

      // Validate step 4 - Financial Details
      if (this.currentStep === 4) {
        if (this.currentYearPaymentStatus === null) {
          this.errors.currentYearPaymentStatus = 'Please confirm whether the property owner has paid for the current year';
        }
      }

      return Object.keys(this.errors).length === 0;
    },

    /**
     * calculateTotals method - Updated to handle current year payment status
     * for BOTH Annual Land Rates AND Annual Ground Rent
     *
     * Key logic:
     * - If NOT PAID for current year: Add annual land rates to land rates arrears
     *                                  Add annual ground rent to ground rent arrears
     * - If PAID for current year: Use only the entered arrears
     */
    calculateTotals() {
      // Calculate annual charges (sum of annual land rates + ground rent)
      this.localProperty.annualCharges =
          (parseFloat(this.localProperty.annualLandRates) || 0) +
          (parseFloat(this.localProperty.annualGroundRent) || 0);

      // Calculate arrears based on current year payment status
      if (this.currentYearPaymentStatus === 'NOT_PAID') {
        // Add current year's annual charges to the entered arrears
        this.localProperty.landRatesArrears =
            (parseFloat(this.enteredLandRatesArrears) || 0) +
            (parseFloat(this.localProperty.annualLandRates) || 0);

        this.localProperty.groundRentArrears =
            (parseFloat(this.enteredGroundRentArrears) || 0) +
            (parseFloat(this.localProperty.annualGroundRent) || 0);
      } else {
        // Use only the entered arrears (paid for current year or not yet selected)
        this.localProperty.landRatesArrears = parseFloat(this.enteredLandRatesArrears) || 0;
        this.localProperty.groundRentArrears = parseFloat(this.enteredGroundRentArrears) || 0;
      }

      // Calculate current balance = sum of ALL arrears components
      this.localProperty.currentBalance =
          (parseFloat(this.localProperty.landRatesArrears) || 0) +
          (parseFloat(this.localProperty.groundRentArrears) || 0) +
          (parseFloat(this.localProperty.accumPenalty) || 0) +
          (parseFloat(this.localProperty.accumInterest) || 0);

      // Keep outstandingArrears synced with currentBalance
      this.localProperty.outstandingArrears = this.localProperty.currentBalance;
    },

    handleSubCountyChange() {
      this.$emit('get-wards', this.selectedSubCounty?.subCountyID);
      this.selectedWard = null; // Reset ward selection
    },

    handleSubmit() {
      // Final validation before submit
      if (this.currentYearPaymentStatus === null) {
        this.errors.currentYearPaymentStatus = 'Please confirm whether the property owner has paid for the current year';
        alert('Please confirm the current year payment status before submitting.');
        return;
      }

      if (!this.validateCurrentStep()) {
        return;
      }

      // Ensure calculations are up to date
      this.calculateTotals();

      // Clear co-owner data if not selected
      if (!this.hasCoOwner) {
        const coOwnerFields = [
          'coOwnerNames', 'coOwnerID', 'coOwnerPassport', 'coOwnerDrivingLicence',
          'coOwnerPassportNonKenyan', 'coOwnerRegistrationCertificate', 'coOwnerPinNumber',
          'coOwnerBoxNo', 'coOwnerPostalCode', 'coOwnerTown', 'coOwnerPhysicalAddress',
          'coOwnerPhoneNo', 'coOwnerEmail'
        ];

        coOwnerFields.forEach(field => {
          this.localProperty[field] = null;
        });
      }

      // Prepare the property data with selected values
      const propertyData = {
        ...this.localProperty,
        block: this.selectedBlock,
        useName: this.selectedUseName,
        subCountyName: this.selectedSubCounty?.subCountyName,
        subCountyID: this.selectedSubCounty?.subCountyID,
        wardName: this.selectedWard?.wardName,
        wardID: this.selectedWard?.wardID,
        // Include the payment status for audit/tracking
        currentYearPaymentStatus: this.currentYearPaymentStatus,
        currentYearPaymentConfirmedYear: this.currentYear,
        // Include the entered arrears (before calculation) for reference
        enteredLandRatesArrears: this.enteredLandRatesArrears,
        enteredGroundRentArrears: this.enteredGroundRentArrears
      };

      this.$emit('submit', propertyData);
    },

    numberFormat(num) {
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(num || 0);
    }
  }
}
</script>

<style scoped>
.step-indicator {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.3s ease;
}

.step-indicator.active {
  background-color: #007bff;
  color: white;
}

.step-indicator.completed {
  background-color: #28a745;
  color: white;
}

.nav-tabs .nav-link {
  border: none;
  border-bottom: 2px solid transparent;
  background: none;
  color: #6c757d;
}

.nav-tabs .nav-link:hover {
  border-color: transparent;
  color: #495057;
  background-color: #f8f9fa;
}

.nav-tabs .nav-link.active {
  color: #495057;
  background-color: transparent;
  border-color: transparent transparent #007bff;
  font-weight: 600;
}

.form-label {
  color: #495057;
  margin-bottom: 0.5rem;
}

.card {
  transition: transform 0.2s ease-in-out;
}

.card:hover {
  transform: translateY(-2px);
}

.step-content {
  min-height: 400px;
}

.modal-xl {
  max-width: 1200px;
}

/* Styling for disabled fields */
.form-select:disabled {
  background-color: #f8f9fa !important;
  color: #6c757d !important;
  border-color: #dee2e6 !important;
  cursor: not-allowed;
}

.bg-light.text-muted {
  color: #6c757d !important;
}

/* Payment option card styles */
.payment-option-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #dee2e6;
}

.payment-option-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.payment-option-card.selected {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

.bg-success-light {
  background-color: rgba(40, 167, 69, 0.1) !important;
}

.bg-danger-light {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.border-2 {
  border-width: 2px !important;
}

.cursor-pointer {
  cursor: pointer;
}

/* Animation for selection */
.payment-option-card .form-check-input:checked ~ .form-check-label i {
  animation: pulse 0.5s ease;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

@media (max-width: 768px) {
  .modal-xl {
    max-width: 95%;
    margin: 1rem auto;
  }

  .step-indicator {
    width: 35px;
    height: 35px;
    font-size: 16px;
  }

  .nav-tabs .nav-link {
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
  }

  .payment-option-card {
    margin-bottom: 1rem;
  }
}
</style>